import React, { useState } from "react";
import { Collapse, Button, Tag, Pagination, Empty, Spin, Input } from "antd";
import {
  PhoneOutlined,
  EnvironmentOutlined,
  DollarOutlined,
  EyeOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { LoadingOutlined } from "@ant-design/icons";
import NumberFormat from "react-number-format";

const AcceptedOrdersList = ({
  orders,
  loading,
  pagination,
  onViewOrder,
  updateFilters,
}) => {
  const [searchTerm, setSearchTerm] = useState("");

  const newAntIcon = (
    <LoadingOutlined style={{ fontSize: 24, color: "#008060" }} spin />
  );

  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case "PENDING":
        return "orange";
      case "IN_TRANSIT":
        return "blue";
      case "DELIVERED":
        return "green";
      case "RETURNED":
        return "red";
      case "ARRIVED":
        return "cyan";
      default:
        return "default";
    }
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    updateFilters({ page: 1, search: value });
  };

  const handlePaginationChange = (page, pageSize) => {
    updateFilters({ page, limit: pageSize });
  };

  if (loading) {
    return <Spin indicator={newAntIcon} />;
  }

  return (
    <div>
      <div className="mb-4">
        <Input
          placeholder="Search by order ref or buyer name..."
          prefix={<SearchOutlined />}
          value={searchTerm}
          onChange={handleSearch}
          allowClear
        />
      </div>

      {orders && orders.length > 0 ? (
        <>
          {/* Desktop View */}
          <div className="hidden md:block">
            <table className="w-full border-collapse border border-gray-300">
              <thead className="bg-gray-100">
                <tr>
                  <th className="border border-gray-300 p-3 text-left">
                    Order Ref
                  </th>
                  <th className="border border-gray-300 p-3 text-left">
                    Restaurant
                  </th>
                  <th className="border border-gray-300 p-3 text-left">
                    Contact
                  </th>
                  <th className="border border-gray-300 p-3 text-left">
                    Pick-up Address
                  </th>
                  <th className="border border-gray-300 p-3 text-left">
                    Status
                  </th>
                  <th className="border border-gray-300 p-3 text-left">
                    Delivery Fee
                  </th>
                  <th className="border border-gray-300 p-3 text-center">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <tr
                    key={order.id}
                    className="border border-gray-300 hover:bg-gray-50"
                  >
                    <td className="border border-gray-300 p-3 font-mono text-lg font-extrabold text-gray-900">
                      {order.orderCode || order.order?.orderCode || "N/A"}
                    </td>
                    <td className="border border-gray-300 p-3">
                      {order.businessName || order.business?.name || "N/A"}
                    </td>
                    <td className="border border-gray-300 p-3">
                      {order.businessPhone || order.business?.phone || "N/A"}
                    </td>
                    <td className="border border-gray-300 p-3 text-sm">
                      {order.order?.pickupAddress || order.pickupAddress || order.business?.pickupAddressString || order.business?.address || "N/A"}
                    </td>
                    <td className="border border-gray-300 p-3">
                      <Tag
                        color={getStatusColor(
                          order.status || order.delivery?.deliveryStatus,
                        )}
                      >
                        {order.status ||
                          order.delivery?.deliveryStatus ||
                          "PENDING"}
                      </Tag>
                    </td>
                    <td className="border border-gray-300 p-3">
                      <NumberFormat
                        thousandSeparator={true}
                        prefix="₦"
                        displayType="text"
                        value={order.deliveryFee || 0}
                      />
                    </td>
                    <td className="border border-gray-300 p-3 text-center">
                      <Button
                        type="primary"
                        size="small"
                        onClick={() => onViewOrder(order)}
                        icon={<EyeOutlined />}
                      >
                        View
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile View */}
          <div className="md:hidden">
            <Collapse>
              {orders.map((order) => (
                <Collapse.Panel
                  key={order.id}
                  header={
                    <div className="flex justify-between items-center w-full">
                      <span className="font-extrabold text-lg text-gray-900">
                        #{order.orderCode || order.order?.orderCode}
                      </span>
                      <Tag
                        color={getStatusColor(
                          order.status || order.delivery?.deliveryStatus,
                        )}
                      >
                        {order.status ||
                          order.delivery?.deliveryStatus ||
                          "PENDING"}
                      </Tag>
                    </div>
                  }
                >
                  <div className="space-y-3">
                    <div className="flex items-start gap-2">
                      <span className="text-sm text-gray-500 min-w-fit">
                        Order Ref:
                      </span>
                      <span className="font-extrabold text-lg text-gray-900">
                        #{order.orderCode || order.order?.orderCode}
                      </span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-sm text-gray-500 min-w-fit">
                        Restaurant:
                      </span>
                      <span className="font-medium">
                        {order.businessName || order.business?.name}
                      </span>
                    </div>
                    <div className="flex items-start gap-2">
                      <PhoneOutlined className="text-gray-400 mt-1" />
                      <div>
                        <div className="text-sm text-gray-500">Contact:</div>
                        <div className="font-medium">
                          {order.businessPhone ||
                            order.business?.phone ||
                            "N/A"}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <EnvironmentOutlined className="text-gray-400 mt-1" />
                      <div>
                        <div className="text-sm text-gray-500">
                          Pick-up Address:
                        </div>
                        <div className="font-medium">
                          {order.order?.pickupAddress ||
                            order.pickupAddress ||
                            order.business?.pickupAddressString ||
                            order.business?.address ||
                            "N/A"}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-sm text-gray-500 min-w-fit">
                        Status:
                      </span>
                      <Tag
                        color={getStatusColor(
                          order.status || order.delivery?.deliveryStatus,
                        )}
                      >
                        {order.status ||
                          order.delivery?.deliveryStatus ||
                          "PENDING"}
                      </Tag>
                    </div>
                    {!order.delivery?.restaurantAcceptedDelivery && (
                      <div className="flex items-start gap-2">
                        <DollarOutlined className="text-gray-400 mt-1" />
                        <div>
                          <div className="text-sm text-gray-500">
                            Delivery Fee:
                          </div>
                          <div className="font-medium">
                            <NumberFormat
                              thousandSeparator={true}
                              prefix="₦"
                              displayType="text"
                              value={parseFloat(
                                order?.deliveryFeeEarnings
                                  ?.deliveryFeeKonnect || 0,
                              ).toFixed(2)}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="mt-4">
                      <Button
                        type="primary"
                        block
                        onClick={() => onViewOrder(order)}
                        icon={<EyeOutlined />}
                      >
                        View Order Details
                      </Button>
                    </div>
                  </div>
                </Collapse.Panel>
              ))}
            </Collapse>
          </div>

          <Pagination
            current={pagination?.page || 1}
            pageSize={pagination?.limit || 10}
            total={pagination?.total || 0}
            onChange={handlePaginationChange}
            showSizeChanger
            showQuickJumper
            showTotal={(total, range) =>
              `${range[0]}-${range[1]} of ${total} orders`
            }
            style={{ marginTop: 16, textAlign: "center" }}
          />
        </>
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No accepted orders"
        />
      )}
    </div>
  );
};

export default AcceptedOrdersList;
